"use client";

import { ValuePropDemoCursor } from "@/components/about/value-prop-demo-cursor";
import {
  type BrainSource,
  type BrainSourceEntry,
  VALUE_PROP_DEMO_PRESS_MS,
  VALUE_PROP_DEMO_RELEASE_MS,
  VALUE_PROP_ICON_ROCK_IDLE_DEG,
  VALUE_PROP_ICON_ROCK_PERIOD_S,
  VALUE_PROP_ICON_ROCK_SELECTED_DEG,
  type ValuePropDemoClickPhase,
} from "@/components/about/value-prop-utils";
import type { CSSProperties } from "react";

type ValuePropSourceIconButtonProps = {
  source: BrainSourceEntry;
  selected: boolean;
  onSelect: (id: BrainSource) => void;
  /** Auto-rotate affordance cursor phase for this icon (idle = none). */
  demoClickPhase?: ValuePropDemoClickPhase;
};

export function ValuePropSourceIconButton({
  source,
  selected,
  onSelect,
  demoClickPhase = "idle",
}: ValuePropSourceIconButtonProps) {
  const rockDeg = selected ? VALUE_PROP_ICON_ROCK_SELECTED_DEG : VALUE_PROP_ICON_ROCK_IDLE_DEG;
  const rockStyle = {
    "--vp-tilt": `${source.tiltDeg}deg`,
    "--vp-rock": `${rockDeg}deg`,
    animation: `value-prop-icon-rock ${VALUE_PROP_ICON_ROCK_PERIOD_S}s ease-in-out infinite`,
  } as CSSProperties;

  const isDemoActive = demoClickPhase !== "idle";
  // One continuous squash→bounce across press+release so the phase change
  // doesn't restart or cut the spring mid-click.
  const pressStyle =
    demoClickPhase === "press" || demoClickPhase === "release"
      ? ({
          animation: `value-prop-demo-icon-press ${VALUE_PROP_DEMO_PRESS_MS + VALUE_PROP_DEMO_RELEASE_MS}ms cubic-bezier(0.22, 1.4, 0.36, 1) both`,
        } as CSSProperties)
      : undefined;

  // Dim/hover only on the glyph — never on the label — so the demo cursor
  // (a sibling) cannot inherit a reduced opacity.
  // Class names must be static strings so Tailwind can detect them.
  // Keep in sync with VALUE_PROP_DIMMED_* / VALUE_PROP_HOVER_* constants.
  const emphasisClass = selected
    ? "opacity-100 scale-100"
    : [
        "opacity-[0.34] scale-[0.82]",
        "group-hover:opacity-[0.78] group-hover:scale-[0.92]",
        "group-focus-within:opacity-[0.78] group-focus-within:scale-[0.92]",
      ].join(" ");

  return (
    <label
      data-testid={`value-prop-icon-${source.id}`}
      data-active={selected ? "true" : "false"}
      data-demo-phase={demoClickPhase}
      className={[
        "group relative cursor-pointer rounded-2xl outline-none opacity-100",
        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-white/70 has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-[rgb(var(--accent-pink-deep))]",
      ].join(" ")}
    >
      <input
        type="radio"
        name="value-prop-brain-source"
        value={source.id}
        checked={selected}
        aria-label={source.label}
        // onClick covers re-pressing the already-selected icon (onChange does not fire then).
        onClick={() => onSelect(source.id)}
        onChange={() => onSelect(source.id)}
        className="sr-only"
      />
      <span
        data-testid={`value-prop-icon-press-${source.id}`}
        className="relative block will-change-transform"
        style={pressStyle}
      >
        <span
          data-testid={`value-prop-icon-emphasis-${source.id}`}
          className={[
            "block transition-[opacity,transform] duration-200 ease-out will-change-transform",
            emphasisClass,
          ].join(" ")}
        >
          <span
            data-testid={`value-prop-icon-rock-${source.id}`}
            data-rock-deg={rockDeg}
            className="block will-change-transform"
            style={rockStyle}
          >
            <img
              src={source.iconSrc}
              alt=""
              aria-hidden="true"
              draggable={false}
              className={[
                "rounded-2xl",
                // Static strings for Tailwind. PlayMath base size is ~1.5× the junk icons.
                source.id === "playmath"
                  ? "h-[7.875rem] w-[7.875rem] sm:h-[11.25rem] sm:w-[11.25rem]"
                  : "h-[5.25rem] w-[5.25rem] sm:h-[7.5rem] sm:w-[7.5rem]",
              ].join(" ")}
            />
          </span>
        </span>
        {/* Sibling of the dimmed glyph — never nested under reduced opacity. */}
        {isDemoActive ? <ValuePropDemoCursor phase={demoClickPhase} /> : null}
      </span>
    </label>
  );
}
